import React, { useMemo, useState, useEffect } from "react";
import { useApp, formatBRL } from "../context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import ContasDashboard from "../components/ContasDashboard";
import PrevisaoCaixa from "../components/PrevisaoCaixa";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, Wallet, Sparkles, ShoppingCart, TrendingUp, CheckCircle2, Clock, Activity, X, Plus, ChevronDown, HandCoins } from "lucide-react";

const COLORS = { mensalidade: "#2563eb", servicos: "#059669", consumos: "#f59e0b", ajustes: "#8b5cf6", recebiveis: "#0ea5e9" };

// Ciclo de fechamento: mês da fatura de um consumo = do fechamento anterior (excl.)
// ao fechamento atual (incl.). Fechamento = dia de pagamento − 5 dias.
const _INTERVALO = { mensal: 1, trimestral: 3, semestral: 6, anual: 12 };
const _absM = (y, m) => y * 12 + m;
const _cicloInfo = (startIso, per, y, m) => {
  const n = _INTERVALO[per || "mensal"] || 1;
  if (n <= 1) return { eh: true, meses: [[y, m]], n: 1 };
  const [sy, sm] = (startIso || "").split("-").map(Number);
  if (!sy) return { eh: true, meses: [[y, m]], n: 1 };
  const idx = _absM(y, m) - _absM(sy, sm);
  if (idx < 0 || idx % n !== (n - 1)) return { eh: false, meses: [], n };
  const base = _absM(y, m); const meses = [];
  for (let k = n - 1; k >= 0; k--) { const a = base - k; meses.push([Math.floor((a - 1) / 12), ((a - 1) % 12) + 1]); }
  return { eh: true, meses, n };
};
const _mesDecimo = (startIso, per, y) => { for (let mm = 12; mm >= 1; mm--) if (_cicloInfo(startIso, per, y, mm).eh) return mm; return null; };
const _diasDe = (r, diasGlobal) => (r && r.fechamentoDiasAntes !== null && Number.isFinite(r.fechamentoDiasAntes)) ? r.fechamentoDiasAntes : (Number.isFinite(diasGlobal) ? diasGlobal : 5);
const _fechDate = (y, m, dia, dias) => { const d = new Date(y, m - 1, Math.min(Number(dia) || 10, 28)); d.setDate(d.getDate() - dias); return d; };
const faturaMesConsumo = (isoData, r, diasGlobal = 5) => {
  const [yy, mm, dd] = (isoData || "").slice(0, 10).split("-").map(Number);
  if (!yy) return (isoData || "").slice(0, 7);
  const dias = _diasDe(r, diasGlobal);
  const d = new Date(yy, mm - 1, dd);
  let y = yy, m = mm;
  if (d > _fechDate(y, m, r?.diaPagamento, dias)) { m += 1; if (m > 12) { m = 1; y += 1; } }
  const per = r?.periodicidadePagamento || "mensal";
  const st = r?.dataInicioContrato || r?.dataInternacao || "";
  let g = 0;
  while (g < 24) { if (_cicloInfo(st, per, y, m).eh) break; m += 1; if (m > 12) { m = 1; y += 1; } g++; }
  return `${y}-${String(m).padStart(2, "0")}`;
};
const MES3 = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const rotuloMes = (ym) => { const [y, m] = ym.split("-"); return `${MES3[+m - 1]}/${y}`; };
const rotuloMesCurto = (ym) => { const [y, m] = ym.split("-"); return `${MES3[+m - 1]}/${y.slice(2)}`; };
const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const mesesEntre = (iniISO, fimISO) => {
  if (!iniISO || !fimISO) return [];
  let [ya, ma] = iniISO.slice(0, 7).split("-").map(Number);
  const [yb, mb] = fimISO.slice(0, 7).split("-").map(Number);
  if (ya > yb || (ya === yb && ma > mb)) return [];
  const out = [];
  while (ya < yb || (ya === yb && ma <= mb)) { out.push(`${ya}-${String(ma).padStart(2, "0")}`); ma++; if (ma > 12) { ma = 1; ya++; } }
  return out;
};
const inputClsA = "px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const somaExtras = (arr, pred) => (arr || []).filter(pred).reduce((s, e) => s + (Number(e.valor) || 0), 0);

const quebra = (f) => {
  const mensalidade = somaExtras(f.extras, (e) => e.descricao.startsWith("Mensalidade") || e.descricao.startsWith("13º"));
  const servicos = somaExtras(f.extras, (e) => e.descricao.startsWith("Serviço:"));
  const consumos = (f.itens || []).reduce((s, c) => s + (Number(c.valorCobrado) || 0), 0);
  const ajustes = (f.manual || []).reduce((s, m) => s + (Number(m.valor) || 0), 0);
  return { mensalidade, servicos, consumos, ajustes, total: Number(f.total) || 0 };
};

const KPI = ({ icon: Icon, label, valor, cor, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="p-2 rounded-xl" style={{ background: `${cor}1a`, color: cor }}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="text-2xl font-bold text-slate-800 mt-2">{valor}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const PainelAnalitico = () => {
  const { faturas, data, getResidente, contasPagar, contasReceber, categoriasConta, orcamentos, salvarOrcamentos } = useApp();
  const mCorrente = mesAtual();
  const anoCorrente = mCorrente.slice(0, 4);

  const [modo, setModo] = useState("mes");
  const [refMes, setRefMes] = useState(mCorrente);
  const [refAno, setRefAno] = useState(anoCorrente);
  const [perIni, setPerIni] = useState(`${mCorrente}-01`);
  const [perFim, setPerFim] = useState(new Date().toISOString().slice(0, 10));
  const [compTipo, setCompTipo] = useState("meses");
  const [compRefs, setCompRefs] = useState([]);
  const [compMesInput, setCompMesInput] = useState(mCorrente);
  const [rateioAberto, setRateioAberto] = useState(false);

  useEffect(() => {
    if (modo !== "comparativo") return;
    if (compTipo === "meses") {
      const [y, m] = mCorrente.split("-").map(Number);
      const ant = new Date(y, m - 2, 1);
      setCompRefs([`${ant.getFullYear()}-${String(ant.getMonth() + 1).padStart(2, "0")}`, mCorrente]);
    } else {
      setCompRefs([String(Number(anoCorrente) - 1), anoCorrente]);
    }
    // eslint-disable-next-line
  }, [modo, compTipo]);

  const anosDisp = Array.from({ length: 5 }, (_, i) => String(Number(anoCorrente) - 4 + i));

  // Buckets p/ o gráfico "faturamento por referência" + conjunto de meses da referência
  const { mesesRef, serieBuckets, refLabel } = useMemo(() => {
    let buckets = [];
    let label = "";
    if (modo === "mes") { buckets = [{ label: rotuloMesCurto(refMes), meses: [refMes] }]; label = `Mês: ${rotuloMes(refMes)}`; }
    else if (modo === "ano") { buckets = Array.from({ length: 12 }, (_, i) => { const m = `${refAno}-${String(i + 1).padStart(2, "0")}`; return { label: MES3[i], meses: [m] }; }); label = `Ano: ${refAno}`; }
    else if (modo === "periodo") { const ms = mesesEntre(perIni, perFim); buckets = ms.map((m) => ({ label: rotuloMesCurto(m), meses: [m] })); label = `Período: ${perIni || "?"} a ${perFim || "?"}`; }
    else if (modo === "comparativo") {
      if (compTipo === "anos") buckets = compRefs.map((y) => ({ label: y, meses: Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, "0")}`) }));
      else buckets = compRefs.map((m) => ({ label: rotuloMesCurto(m), meses: [m] }));
      label = `Comparativo: ${buckets.map((b) => b.label).join("  ×  ")}`;
    }
    const todos = Array.from(new Set(buckets.flatMap((b) => b.meses)));
    return { mesesRef: todos, serieBuckets: buckets, refLabel: label };
  }, [modo, refMes, refAno, perIni, perFim, compTipo, compRefs]);

  const addCompMes = () => { if (compMesInput && !compRefs.includes(compMesInput)) setCompRefs((r) => [...r, compMesInput].sort()); };
  const toggleCompAno = (y) => setCompRefs((r) => r.includes(y) ? r.filter((x) => x !== y) : [...r, y].sort());
  const removerRef = (ref) => setCompRefs((r) => r.filter((x) => x !== ref));

  const info = useMemo(() => {
    const mesesSet = new Set(mesesRef);
    const resById = Object.fromEntries((data?.residentes || []).map((r) => [r.id, r]));
    const pagas = data?.faturasPagas || {};
    const residentesAtivos = (data?.residentes || []).filter((r) => (r.status || "Ativo") === "Ativo");
    const ativos = residentesAtivos.length;

    // Reajuste (mesma regra do faturamento): o valor do contrato é o ATUAL, +8% a cada aniversário.
    const mensVig = (dataInicio, base, ano, mes) => {
      const b = Number(base) || 0; if (!b) return 0;
      const [sy, sm] = (dataInicio || "").split("-").map(Number);
      if (!sy) return b;
      const hj = new Date();
      const aAlvo = Math.floor(((ano * 12 + mes) - (sy * 12 + sm)) / 12);
      const aHoje = Math.max(0, Math.floor(((hj.getFullYear() * 12 + (hj.getMonth() + 1)) - (sy * 12 + sm)) / 12));
      return +(b * Math.pow(1.08, aAlvo - aHoje)).toFixed(2);
    };

    // PROVISÃO por residente para um conjunto de meses (independe de fatura/boleto).
    const computeAcc = (mesesArr) => {
      const set = new Set(mesesArr);
      const acc = {};
      residentesAtivos.forEach((r) => { acc[r.id] = { key: r.id, residenteId: r.id, nome: r.nome, mensalidade: 0, servicos: 0, consumos: 0, ajustes: 0 }; });
      // Mensalidade + 13º provisionados (respeitando a periodicidade do contrato)
      residentesAtivos.forEach((r) => {
        const base = Number(r.valorMensalidade) || 0; if (base <= 0) return;
        const reaj = r.dataInicioContrato || r.dataInternacao || "";
        const inicio = r.dataInternacao || r.dataInicioContrato || "";
        const [iy, im] = inicio.split("-").map(Number);
        const per = r.periodicidadePagamento || "mensal";
        const st = r.dataInicioContrato || r.dataInternacao || "";
        mesesArr.forEach((ym) => {
          const [y, m] = ym.split("-").map(Number);
          const info = _cicloInfo(st, per, y, m);
          if (!info.eh) return;
          info.meses.forEach(([yy, mm]) => {
            if (iy && _absM(yy, mm) < _absM(iy, im || 1)) return;
            acc[r.id].mensalidade += mensVig(reaj, base, yy, mm);
          });
          if (r.cobrarDecimoTerceiro && _mesDecimo(st, per, y) === m) {
            const integral = info.n > 1;
            let meses = integral ? 12 : (iy === y ? Math.max(0, 13 - (im || 1)) : 12);
            if (meses > 0) acc[r.id].mensalidade += +(((mensVig(reaj, base, y, 12)) / 12) * meses).toFixed(2);
          }
        });
      });
      // Serviços (recorrentes provisionados × ciclo + avulsos no ciclo)
      (data?.residenteServicos || []).forEach((s) => {
        if (s.ativo === false) return;
        const r = resById[s.residenteId]; if (!r || (r.status || "Ativo") !== "Ativo" || !acc[s.residenteId]) return;
        const base = Number(s.valor) || 0; if (base <= 0) return;
        const reaj = r.dataInicioContrato || r.dataInternacao || "";
        const per = r.periodicidadePagamento || "mensal";
        const st = r.dataInicioContrato || r.dataInternacao || "";
        if (s.recorrente) {
          const [ay, am] = (s.dataAdicao || "").split("-").map(Number);
          mesesArr.forEach((ym) => {
            const [y, m] = ym.split("-").map(Number);
            const info = _cicloInfo(st, per, y, m); if (!info.eh) return;
            info.meses.forEach(([yy, mm]) => { if (ay && _absM(yy, mm) >= _absM(ay, am || 1)) acc[s.residenteId].servicos += mensVig(reaj, base, yy, mm); });
          });
        } else if (s.cobrarMes) {
          const [cy0, cm0] = s.cobrarMes.split("-").map(Number);
          let y = cy0, m = cm0, g = 0;
          while (g < 24) { if (_cicloInfo(st, per, y, m).eh) break; m += 1; if (m > 12) { m = 1; y += 1; } g++; }
          if (set.has(`${y}-${String(m).padStart(2, "0")}`)) acc[s.residenteId].servicos += base;
        }
      });
      // Consumos reais (atribuídos ao mês de cobrança do ciclo)
      (data?.consumos || []).forEach((c) => {
        if ((Number(c.valorCobrado) || 0) <= 0 || !acc[c.residenteId]) return;
        const mesFat = faturaMesConsumo(c.data, resById[c.residenteId], data?.fechamentoDiasAntes);
        if (!set.has(mesFat)) return;
        acc[c.residenteId].consumos += Number(c.valorCobrado) || 0;
      });
      // Ajustes manuais
      Object.entries(data?.faturasAjustes || {}).forEach(([key, aj]) => {
        const idx = key.lastIndexOf("_"); const rid = key.slice(0, idx); const mes = key.slice(idx + 1);
        if (!set.has(mes) || !acc[rid]) return;
        (aj.itens || []).forEach((it) => { acc[rid].ajustes += Number(it.valor) || 0; });
      });
      return acc;
    };

    const accRef = computeAcc(mesesRef);
    const linhas = Object.values(accRef).map((a) => {
      const total = +(a.mensalidade + a.servicos + a.consumos + a.ajustes).toFixed(2);
      const paga = mesesRef.length === 1 ? !!pagas[`${a.residenteId}_${mesesRef[0]}`] : false;
      return { ...a, mensalidade: +a.mensalidade.toFixed(2), servicos: +a.servicos.toFixed(2), consumos: +a.consumos.toFixed(2), ajustes: +a.ajustes.toFixed(2), total, paga };
    }).filter((l) => l.total > 0).sort((a, b) => b.total - a.total);

    // Outras contas a receber (recebíveis manuais) — por mês de vencimento. NÃO inclui produtos/consumos
    // (que já entram nas faturas via "consumos"), evitando dupla contagem.
    const recebiveisDe = (mesesArr) => {
      const set = new Set(mesesArr);
      return (contasReceber || []).filter((c) => set.has((c.vencimento || "").slice(0, 7))).reduce((s, c) => s + (Number(c.valor) || 0), 0);
    };
    const recebiveis = +recebiveisDe(mesesRef).toFixed(2);

    let mensalidade = 0, servicos = 0, consumos = 0, ajustes = 0, total = 0;
    linhas.forEach((l) => { mensalidade += l.mensalidade; servicos += l.servicos; consumos += l.consumos; ajustes += l.ajustes; total += l.total; });
    total = +(total + recebiveis).toFixed(2);
    const nPagas = linhas.filter((l) => l.paga).length;
    const nPend = linhas.length - nPagas;

    // Faturamento provisionado por referência (barra) — inclui recebíveis manuais
    const serieMes = serieBuckets.map((b) => {
      const acc = computeAcc(b.meses);
      const valor = Object.values(acc).reduce((s, a) => s + a.mensalidade + a.servicos + a.consumos + a.ajustes, 0) + recebiveisDe(b.meses);
      return { mes: b.label, valor: +valor.toFixed(2) };
    });

    const composicao = [
      { name: "Mensalidades", value: +mensalidade.toFixed(2), key: "mensalidade" },
      { name: "Serviços", value: +servicos.toFixed(2), key: "servicos" },
      { name: "Consumos", value: +consumos.toFixed(2), key: "consumos" },
      { name: "Ajustes", value: +ajustes.toFixed(2), key: "ajustes" },
      { name: "Contas a receber", value: recebiveis, key: "recebiveis" },
    ].filter((c) => c.value > 0);

    const topServicos = linhas.filter((l) => l.servicos > 0).slice(0, 8).map((l) => ({ nome: (l.nome.split(" ")[0] || l.nome), valor: l.servicos }));

    // ----- RATEIO de custos (contas a pagar do período) proporcional à mensalidade provisionada -----
    const custosMes = (contasPagar || []).filter((c) => mesesSet.has((c.vencimento || "").slice(0, 7))).reduce((s, c) => s + (Number(c.valor) || 0), 0);
    const rateio = linhas.filter((l) => l.mensalidade > 0).map((l) => {
      const custo = mensalidade > 0 ? custosMes * (l.mensalidade / mensalidade) : 0;
      return { key: l.key, nome: l.nome, mensalidade: l.mensalidade, custo, resultado: l.mensalidade - custo };
    }).sort((a, b) => a.resultado - b.resultado);
    const noPrejuizo = rateio.filter((r) => r.resultado < 0);
    const resultadoGeral = mensalidade - custosMes;

    // ----- PREVISIBILIDADE anual (mensalidades*12 + 13º + serviços recorrentes*12) -----
    let anualMens = 0, anual13 = 0;
    residentesAtivos.forEach((r) => {
      const base = Number(r.valorMensalidade) || 0;
      anualMens += base * 12;
      if (r.cobrarDecimoTerceiro) anual13 += base;
    });
    const anualServ = (data?.residenteServicos || []).filter((s) => s.ativo !== false && s.recorrente).reduce((s, x) => s + (Number(x.valor) || 0) * 12, 0);
    const anualTotal = anualMens + anual13 + anualServ;

    // ----- PONTO DE EQUILÍBRIO (break-even) -----
    const receitaOperacional = mensalidade + servicos + recebiveis; // mensalidades + serviços + outras contas a receber
    const nComMens = rateio.length;
    const mensalidadeMedia = nComMens > 0 ? mensalidade / nComMens : 0;
    const residentesNecessarios = mensalidadeMedia > 0 ? Math.ceil(custosMes / mensalidadeMedia) : 0;
    const receitaMinima = custosMes;
    const coberto = receitaOperacional >= custosMes;
    const faltamResidentes = Math.max(0, residentesNecessarios - nComMens);
    const coberturaPct = custosMes > 0 ? Math.min(100, (receitaOperacional / custosMes) * 100) : (receitaOperacional > 0 ? 100 : 0);

    return { linhas, mensalidade, servicos, consumos, ajustes, recebiveis, total, nPagas, nPend, serieMes, composicao, topServicos, ativos,
      custosMes, rateio, noPrejuizo, resultadoGeral, anualMens, anual13, anualServ, anualTotal, mediaMensal: anualTotal / 12,
      mensalidadeMedia, residentesNecessarios, receitaMinima, coberto, faltamResidentes, coberturaPct, nComMens };
  }, [faturas, data, getResidente, contasPagar, contasReceber, mesesRef, serieBuckets]);

  const mesLabel = refLabel;

  const recebMes = useMemo(() => {
    const by = {};
    const bump = (mes, pago, v) => { if (!mes) return; if (!by[mes]) by[mes] = { mes, pago: 0, aberto: 0 }; if (pago) by[mes].pago += v; else by[mes].aberto += v; };
    (faturas || []).forEach((f) => { if (!f.cancelada) bump(f.mes, f.paga, f.total); });
    (contasReceber || []).forEach((c) => bump((c.vencimento || "").slice(0, 7), c.status === "recebido", Number(c.valor) || 0));
    return Object.values(by).sort((a, b) => a.mes.localeCompare(b.mes)).slice(-12)
      .map((d) => ({ label: `${d.mes.slice(5)}/${d.mes.slice(2, 4)}`, Recebido: +d.pago.toFixed(2), "Em aberto": +d.aberto.toFixed(2) }));
  }, [faturas, contasReceber]);

  return (
    <div className="space-y-6" data-testid="painel-analitico-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Activity className="w-6 h-6 text-blue-600" /> Painel Analítico</h1>
        <p className="text-sm text-slate-500">Visão geral da operação e das finanças — referência: <b>{refLabel}</b>.</p>
      </div>

      {/* Barra de referência */}
      <div className="bg-white rounded-2xl border border-slate-100 p-3 shadow-sm flex flex-wrap items-center gap-3" data-testid="ref-bar">
        <div className="w-48"><Select value={modo} onValueChange={setModo}>
          <SelectTrigger data-testid="pa-modo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mes">Por mês</SelectItem>
            <SelectItem value="ano">Por ano</SelectItem>
            <SelectItem value="comparativo">Comparativo</SelectItem>
            <SelectItem value="periodo">Período (calendário)</SelectItem>
          </SelectContent>
        </Select></div>
        {modo === "mes" && (
          <div className="flex items-center gap-2"><label className="text-sm text-slate-500">Mês:</label>
            <input type="month" className={inputClsA} value={refMes} onChange={(e) => setRefMes(e.target.value)} data-testid="pa-ref-mes" /></div>
        )}
        {modo === "ano" && (
          <div className="w-32"><Select value={refAno} onValueChange={setRefAno}>
            <SelectTrigger data-testid="pa-ref-ano"><SelectValue /></SelectTrigger>
            <SelectContent>{anosDisp.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select></div>
        )}
        {modo === "periodo" && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm text-slate-500">De:</label>
            <input type="date" className={inputClsA} value={perIni} onChange={(e) => setPerIni(e.target.value)} data-testid="pa-ini" />
            <label className="text-sm text-slate-500">até:</label>
            <input type="date" className={inputClsA} value={perFim} onChange={(e) => setPerFim(e.target.value)} data-testid="pa-fim" />
          </div>
        )}
        {modo === "comparativo" && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-36"><Select value={compTipo} onValueChange={(v) => { setCompTipo(v); setCompRefs([]); }}>
              <SelectTrigger data-testid="pa-comp-tipo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="meses">Comparar meses</SelectItem><SelectItem value="anos">Comparar anos</SelectItem></SelectContent>
            </Select></div>
            {compTipo === "meses" ? (
              <>
                <input type="month" className={inputClsA} value={compMesInput} onChange={(e) => setCompMesInput(e.target.value)} data-testid="pa-comp-mes-input" />
                <Button variant="outline" size="sm" className="gap-1" onClick={addCompMes} data-testid="pa-comp-add"><Plus className="w-4 h-4" /> Adicionar</Button>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {compRefs.map((r) => <span key={r} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md">{rotuloMesCurto(r)}<button onClick={() => removerRef(r)}><X className="w-3 h-3" /></button></span>)}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                {anosDisp.map((y) => (
                  <button key={y} onClick={() => toggleCompAno(y)} data-testid={`pa-comp-ano-${y}`}
                    className={`text-xs px-2.5 py-1.5 rounded-md border ${compRefs.includes(y) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{y}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <span className="text-xs text-slate-400 ml-auto">{refLabel}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Residentes ativos" valor={info.ativos} cor="#0ea5e9" testid="kpi-ativos" />
        <KPI icon={Wallet} label="Faturamento previsto (mês)" valor={formatBRL(info.total)} cor="#2563eb" sub={`${info.linhas.length} faturas`} testid="kpi-faturamento" />
        <KPI icon={Sparkles} label="Serviços extras (mês)" valor={formatBRL(info.servicos)} cor="#059669" testid="kpi-servicos" />
        <KPI icon={ShoppingCart} label="Consumos (mês)" valor={formatBRL(info.consumos)} cor="#f59e0b" testid="kpi-consumos" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 lg:col-span-2">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-blue-600" /> Faturamento previsto por mês</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={info.serieMes} margin={{ left: 4, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatBRL(v)} />
              <Bar dataKey="valor" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 mb-4">Composição do mês</h2>
          {info.composicao.length === 0 ? <p className="text-sm text-slate-400">Sem valores neste mês.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={info.composicao} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} label={(e) => e.name}>
                  {info.composicao.map((c) => <Cell key={c.key} fill={COLORS[c.key]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatBRL(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI icon={CheckCircle2} label="Faturas pagas (mês)" valor={info.nPagas} cor="#059669" testid="kpi-pagas" />
        <KPI icon={Clock} label="Faturas pendentes (mês)" valor={info.nPend} cor="#ef4444" testid="kpi-pendentes" />
        <KPI icon={Wallet} label="Mensalidades (mês)" valor={formatBRL(info.mensalidade)} cor="#6366f1" testid="kpi-mensalidades" />
        <KPI icon={HandCoins} label="Contas a receber (mês)" valor={formatBRL(info.recebiveis)} cor="#0ea5e9" testid="kpi-recebiveis" />
        <KPI icon={Sparkles} label="Ajustes manuais (mês)" valor={formatBRL(info.ajustes)} cor="#8b5cf6" testid="kpi-ajustes" />
      </div>

      {/* PREVISIBILIDADE ANUAL */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="previsibilidade-anual">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-blue-600" /> Previsibilidade anual (provisão de recebimento)</h2>
        <p className="text-xs text-slate-400 mb-4">Mensalidades × 12 + 13º dos contratos que preveem + serviços extras recorrentes × 12.</p>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">Mensalidades (12x)</div><div className="text-lg font-bold text-slate-800" data-testid="prev-mensalidades">{formatBRL(info.anualMens)}</div></div>
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">13º (provisão)</div><div className="text-lg font-bold text-slate-800" data-testid="prev-decimo">{formatBRL(info.anual13)}</div></div>
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">Serviços recorrentes (12x)</div><div className="text-lg font-bold text-emerald-700" data-testid="prev-servicos">{formatBRL(info.anualServ)}</div></div>
          <div className="rounded-xl bg-blue-600 text-white p-4"><div className="text-xs opacity-90">Total geral do ano</div><div className="text-lg font-bold" data-testid="prev-total-ano">{formatBRL(info.anualTotal)}</div></div>
          <div className="rounded-xl bg-emerald-600 text-white p-4"><div className="text-xs opacity-90">Média provisionada/mês</div><div className="text-lg font-bold" data-testid="prev-media-mes">{formatBRL(info.mediaMensal)}</div></div>
        </div>
      </div>

      {/* RATEIO de custos por residente */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="rateio-custos">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Wallet className="w-4 h-4 text-violet-600" /> Rateio de custos — {mesLabel}</h2>
          <div className="flex gap-4 text-sm">
            <span className="text-slate-500">Custos do mês: <b className="text-rose-600">{formatBRL(info.custosMes)}</b></span>
            <span className="text-slate-500">Receita mensalidades: <b className="text-emerald-700">{formatBRL(info.mensalidade)}</b></span>
            <span className="text-slate-500">Resultado: <b className={info.resultadoGeral >= 0 ? "text-emerald-700" : "text-rose-600"}>{formatBRL(info.resultadoGeral)}</b></span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-4">Os custos de Contas a Pagar do mês são rateados proporcionalmente à mensalidade de cada residente. Residentes em vermelho <b>não cobrem</b> o custo rateado.</p>
        {info.noPrejuizo.length > 0 && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3" data-testid="rateio-alerta-prejuizo">
            ⚠️ {info.noPrejuizo.length} residente(s) com mensalidade abaixo do custo rateado: {info.noPrejuizo.slice(0, 5).map((r) => r.nome).join(", ")}{info.noPrejuizo.length > 5 ? "…" : ""}
          </div>
        )}
        <button onClick={() => setRateioAberto((v) => !v)} className="flex items-center gap-2 text-sm font-medium text-violet-700 hover:text-violet-800 mb-2" data-testid="rateio-toggle">
          <ChevronDown className={`w-4 h-4 transition-transform ${rateioAberto ? "rotate-180" : ""}`} />
          {rateioAberto ? "Ocultar" : "Ver"} rateio por residente ({info.rateio.length})
        </button>
        {rateioAberto && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3">Residente</th>
              <th className="py-2 px-3 text-right">Mensalidade</th>
              <th className="py-2 px-3 text-right">Custo rateado</th>
              <th className="py-2 px-3 text-right">Resultado</th>
            </tr></thead>
            <tbody>
              {info.rateio.length === 0 && <tr><td colSpan={4} className="py-4 text-slate-400">Sem mensalidades cadastradas para ratear.</td></tr>}
              {info.rateio.map((r) => (
                <tr key={r.key} className={`border-b border-slate-50 ${r.resultado < 0 ? "bg-rose-50/40" : ""}`} data-testid={`rateio-row-${r.key}`}>
                  <td className="py-2.5 pr-3 font-medium text-slate-700">{r.nome}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{formatBRL(r.mensalidade)}</td>
                  <td className="py-2.5 px-3 text-right text-rose-600">{formatBRL(r.custo)}</td>
                  <td className={`py-2.5 px-3 text-right font-bold ${r.resultado < 0 ? "text-rose-600" : "text-emerald-700"}`}>{formatBRL(r.resultado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* PONTO DE EQUILÍBRIO */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="ponto-equilibrio">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><Activity className="w-4 h-4 text-amber-600" /> Ponto de equilíbrio — {mesLabel}</h2>
        <p className="text-xs text-slate-400 mb-4">Quanto de receita/quantos residentes são necessários para cobrir os custos do mês (Contas a Pagar).</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">Custos fixos do mês</div><div className="text-lg font-bold text-rose-600" data-testid="pe-custos">{formatBRL(info.custosMes)}</div></div>
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">Receita mínima p/ equilíbrio</div><div className="text-lg font-bold text-slate-800" data-testid="pe-receita-minima">{formatBRL(info.receitaMinima)}</div></div>
          <div className="rounded-xl bg-slate-50 p-4"><div className="text-xs text-slate-500">Residentes necessários</div><div className="text-lg font-bold text-slate-800" data-testid="pe-residentes-necessarios">{info.residentesNecessarios}</div><div className="text-[11px] text-slate-400">média {formatBRL(info.mensalidadeMedia)}/mês</div></div>
          <div className={`rounded-xl p-4 text-white ${info.coberto ? "bg-emerald-600" : "bg-rose-600"}`}><div className="text-xs opacity-90">{info.coberto ? "Custos cobertos" : "Faltam residentes"}</div><div className="text-lg font-bold" data-testid="pe-status">{info.coberto ? "✔ Equilíbrio atingido" : `+${info.faltamResidentes} residente(s)`}</div></div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Cobertura dos custos pela receita atual ({info.nComMens} c/ mensalidade)</span><span className="font-semibold">{info.coberturaPct.toFixed(0)}%</span></div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${info.coberto ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${info.coberturaPct}%` }} data-testid="pe-barra" /></div>
        </div>
      </div>

      {/* Modo Conferência: fatura de cada residente ANTES de gerar boleto */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="conferencia-faturas">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-800">Conferência de faturas — {mesLabel}</h2>
          <span className="text-xs text-slate-400">Valores previstos (nenhum boleto é gerado aqui)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">Residente</th>
                <th className="py-2 px-3 text-right">Mensalidade</th>
                <th className="py-2 px-3 text-right">Serviços</th>
                <th className="py-2 px-3 text-right">Consumos</th>
                <th className="py-2 px-3 text-right">Ajustes</th>
                <th className="py-2 px-3 text-right">Total</th>
                <th className="py-2 pl-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {info.linhas.length === 0 && <tr><td colSpan={7} className="py-4 text-slate-400">Nenhuma fatura prevista para este mês.</td></tr>}
              {info.linhas.map((l) => (
                <tr key={l.key} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`conf-row-${l.key}`}>
                  <td className="py-2.5 pr-3 font-medium text-slate-700">{l.nome}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{formatBRL(l.mensalidade)}</td>
                  <td className="py-2.5 px-3 text-right font-medium text-emerald-700" data-testid={`conf-servicos-${l.key}`}>{l.servicos ? formatBRL(l.servicos) : "—"}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{l.consumos ? formatBRL(l.consumos) : "—"}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{l.ajustes ? formatBRL(l.ajustes) : "—"}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-slate-800">{formatBRL(l.total)}</td>
                  <td className="py-2.5 pl-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${l.paga ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{l.paga ? "Paga" : "Prevista"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            {info.linhas.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-100 font-semibold text-slate-800">
                  <td className="py-2.5 pr-3">Total geral</td>
                  <td className="py-2.5 px-3 text-right">{formatBRL(info.mensalidade)}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-700">{formatBRL(info.servicos)}</td>
                  <td className="py-2.5 px-3 text-right">{formatBRL(info.consumos)}</td>
                  <td className="py-2.5 px-3 text-right">{formatBRL(info.ajustes)}</td>
                  <td className="py-2.5 px-3 text-right">{formatBRL(info.total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {/* Contas a Receber — recebimentos por mês (movido de Faturas) */}
      <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid="painel-recebimentos">
        <h2 className="font-semibold text-slate-800 mb-4">Contas a Receber — Recebimentos por mês</h2>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={recebMes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatBRL(v)} />
              <Legend />
              <Bar dataKey="Recebido" stackId="a" fill="#16a34a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Em aberto" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Previsão de caixa: receitas projetadas x despesas lançadas */}
      <PrevisaoCaixa residentes={data?.residentes || []} residenteServicos={data?.residenteServicos || []} faturas={faturas || []} contasPagar={contasPagar || []} contasReceber={contasReceber || []} />

      {/* Contas a Pagar — análises (movido de Contas a Pagar) */}
      <div data-testid="painel-contas-pagar">
        <h2 className="font-semibold text-slate-800 mb-3">Contas a Pagar — Análises</h2>
        <ContasDashboard contasPagar={contasPagar || []} faturas={faturas || []} categoriasConta={categoriasConta || []} orcamentos={orcamentos || {}} salvarOrcamentos={salvarOrcamentos} contasReceber={contasReceber || []} />
      </div>
    </div>
  );
};

export default PainelAnalitico;